/**
 * Sentry Service
 * 
 * Initializes Sentry for error tracking and provides utilities
 * for manual error reporting with game context.
 */
import * as Sentry from '@sentry/react';
import { Capacitor } from '@capacitor/core';
import { debugLog, debugError, debugWarn, debugTable, debugGroup, debugGroupEnd } from '../utils/debugUtils';

// Check if Sentry is initialized
let isInitialized = false;

/**
 * Initialize Sentry with the configured DSN
 * Should be called once at app startup, before React renders
 */
export const initSentry = () => {
    if (isInitialized) {
        console.warn('[SentryService] Already initialized');
        return;
    }

    const dsn = process.env.REACT_APP_SENTRY_DSN;

    if (!dsn) {
        console.warn('[SentryService] No DSN configured, Sentry will not be initialized');
        return;
    }

    const tracesSampleRate = parseFloat(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE) || 0.1;

    try {
        Sentry.init({
            dsn,
            tracesSampleRate,
            environment: process.env.NODE_ENV || 'development',
            release: `rondaybaciga@${process.env.REACT_APP_VERSION || '1.0.0'}`,

            // Transport configuration for better reliability
            transportOptions: {
                // Increase timeout for slow connections
                fetchOptions: {
                    timeout: 10000,
                },
            },

            // Additional configuration
            beforeSend(event) {
                // Remove any potentially sensitive data
                if (event.user) {
                    delete event.user.ip_address;
                    delete event.user.email;
                }
                return event;
            },

            // Add platform info
            tags: {
                platform: Capacitor.isNativePlatform() ? 'native' : 'web',
                nativePlatform: Capacitor.getPlatform(),
            },
        });

        isInitialized = true;
        debugLog('[SentryService] Initialized successfully');
    } catch (error) {
        console.error('[SentryService] Failed to initialize:', error);
    }
};

/**
 * Capture an exception with game context
 * 
 * @param {Error} error - The error to capture
 * @param {Object} context - Additional context
 */
export const captureException = (error, context = {}) => {
    if (!isInitialized) {
        console.error('[SentryService] Not initialized, logging error locally:', error);
        return;
    }

    Sentry.withScope((scope) => {
        Object.entries(context).forEach(([key, value]) => {
            scope.setExtra(key, value);
        });
        Sentry.captureException(error);
    });
};

/**
 * Check if Sentry is initialized and working
 * @returns {boolean}
 */
export const isSentryReady = () => isInitialized;

export default {
    initSentry,
    captureException,
    isSentryReady,
};
